import type { UseEveAgentOptions, UseEveAgentReturn } from 'eve/vue'
import type { ClientSessionState, SessionStreamEvent } from 'eve/client'

import { useEveAgent } from 'eve/vue'

type Options = UseEveAgentOptions
type Agent = UseEveAgentReturn

/** Native eve streaming, with a replaceable Vue scope for workspace switches. */
export function useResumableEveAgent(options: Options = {}) {
  let scope = effectScope()
  const createAgent = (
    events: readonly SessionStreamEvent[],
    session?: ClientSessionState,
  ) => scope.run(() => useEveAgent({
    ...options,
    initialEvents: events,
    initialSession: session,
    resume: import.meta.client && !!session,
  }))!

  const agent = shallowRef<Agent>(
    createAgent(options.initialEvents ?? [], options.initialSession),
  )

  function reset(state?: {
    events?: readonly SessionStreamEvent[]
    session?: ClientSessionState
  }) {
    // Disposing only detaches transport; the old workspace's durable turn continues.
    scope.stop()
    scope = effectScope(true)
    agent.value = createAgent(state?.events ?? [], state?.session)
  }

  onScopeDispose(() => scope.stop())

  return {
    cancel: () => agent.value.cancel(),
    agents: computed(() => agent.value.agents.value),
    messages: computed(() => agent.value.messages.value),
    view: computed(() => agent.value.view.value),
    newSession: () => agent.value.newSession(),
    prewarm: () => agent.value.prewarm(),
    error: computed(() => agent.value.error.value),
    events: computed(() => agent.value.events.value),
    reset,
    respond: ((...args) => agent.value.respond(...args)) as Agent['respond'],
    resume: () => agent.value.resume(),
    send: ((...args) => agent.value.send(...args)) as Agent['send'],
    session: computed(() => agent.value.session.value),
    status: computed(() => agent.value.status.value),
  }
}
